/**
 * @file UiConfig.ts
 * @role Loads and validates UI theme/layout tunables. Visual constants live here so scene
 *       and view classes consume named policy instead of scattering magic numbers.
 * @owns The validated UI config shape.
 * @reads Bundled ui.config.json or injected raw config in tests.
 * @neverDoes Render Pixi objects. Read viewport state. Decide gameplay.
 * @invariants 10 (validate before use), 14 (layout policy is explicit).
 * @see docs/CONFIG_GUIDE.md, src/config/ui.config.json
 */
import { z } from 'zod';
import uiConfigRaw from '@config/ui.config.json';
import { GameError } from '@core/errors/GameError';

const color = z.number().int().min(0).max(0xffffff);
const positive = z.number().positive();
const nonNegative = z.number().nonnegative();
const positiveInt = z.number().int().positive();
const alpha = z.number().min(0).max(1);
const sideMarginRatio = z.number().min(0).max(0.3);
const readoutXRatio = z.number().positive().max(1);
const statsRailTypographySchema = z.object({
  captionFontSize: positive,
  valueFontSize: positive,
  captionLineHeight: positive,
  valueLineHeight: positive,
  labelValueGap: nonNegative,
});
const titleEffectCommand = z.enum(['none', 'shake', 'shakeScale', 'punch', 'glowPulse']);
const titleFontPresetSchema = z.object({
  id: z.string().min(1).max(32),
  label: z.string().min(1).max(48),
  assetId: z.string().min(1),
  fontFamily: z.string().min(1),
  fallbackFamily: z.string().min(1),
  weight: z.union([
    z.literal(400),
    z.literal(500),
    z.literal(600),
    z.literal(700),
    z.literal(800),
    z.literal(900),
  ]),
});
const titleStylePresetSchema = z.object({
  id: z.string().min(1).max(32),
  label: z.string().min(1).max(48),
  titleFontId: z.string().min(1),
  subtitleFontId: z.string().min(1),
  titleScale: z.number().min(0.7).max(1.35),
  subtitleScale: z.number().min(0.7).max(1.35),
  letterSpacing: z.number().min(0).max(16),
  lineGap: z.number().min(0).max(32),
  alignment: z.enum(['left', 'center', 'right']),
  titleColor: color,
  titleStrokeColor: color,
  titleStrokeWidth: z.number().min(0).max(16),
  titleShadowColor: color,
  titleShadowAlpha: alpha,
  titleShadowBlur: z.number().min(0).max(20),
  titleShadowDistance: z.number().min(0).max(20),
  subtitleColor: color,
  subtitleStrokeColor: color,
  subtitleStrokeWidth: z.number().min(0).max(12),
  ribbonVisible: z.boolean(),
  ribbonColor: color,
  ribbonAlpha: alpha,
  ribbonStrokeColor: color,
  ribbonStrokeWidth: z.number().min(0).max(8),
  ribbonWidth: z.number().positive().max(420),
  ribbonHeight: z.number().positive().max(100),
  ribbonRadius: z.number().min(0).max(40),
  ornamentsVisible: z.boolean(),
  ornamentColor: color,
  ornamentLength: z.number().min(0).max(100),
  ornamentGap: z.number().min(0).max(60),
  ornamentDotRadius: z.number().min(0).max(12),
});
const titleAnimationPresetSchema = z.object({
  id: z.string().min(1).max(32),
  label: z.string().min(1).max(48),
  durationMs: z.number().int().min(80).max(2_000),
  staggerMs: z.number().int().min(0).max(300),
  amplitudePx: z.number().min(0).max(24),
  scalePeak: z.number().min(1).max(1.35),
  overshoot: z.number().min(0).max(3),
  oscillations: z.number().int().min(1).max(8),
  holdMs: z.number().int().min(0).max(1_000),
  easing: z.enum(['linear', 'easeOutCubic', 'easeOutBack']),
});
const neonSequenceKind = z.enum([
  'fadeTrail',
  'shower',
  'bubbleRise',
  'sideSweep',
  'orbitPulse',
  'jackpotCascade',
]);

const neonPlacementSchema = z.object({
  id: z.string().min(1).max(40),
  textureId: z.string().min(1),
  groups: z.array(z.string().min(1).max(32)).min(1).max(6),
  /** Position in the uncropped background art, mapped through the same focal cover transform. */
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  /** Width as a fraction of the source background width. */
  sizeRatio: z.number().positive().max(0.3),
  rotationDeg: z.number().min(-45).max(45).default(0),
  /** Deterministic variation within the selected movement profile. */
  fallSpeedMultiplier: z.number().min(0.5).max(1.5),
  fallPhase: z.number().min(0).max(1),
  driftPhase: z.number().min(0).max(1),
});

const neonQualitySchema = z.object({
  visible: z.boolean(),
  animated: z.boolean(),
  alphaMultiplier: alpha,
  maxActiveTargets: z.number().int().min(0).max(16),
});

const neonSequenceSchema = z.object({
  id: z.string().min(1).max(40),
  kind: neonSequenceKind,
  /** `all` or one of the placement group names. */
  group: z.string().min(1).max(32),
  durationMs: z.number().int().positive().max(4_000),
  staggerMs: z.number().int().min(0).max(1_000),
  distanceXRatio: z.number().min(-0.5).max(0.5),
  distanceYRatio: z.number().min(-1).max(1),
  scalePeak: z.number().min(1).max(1.3),
  cycles: z.number().int().min(1).max(6),
});

const neonMovementProfileSchema = z.object({
  direction: z.enum(['up', 'down']),
  /** Fraction of the viewport travelled each second before per-fruit variation. */
  speedRatioPerSecond: z.number().positive().max(0.25),
  driftRatio: z.number().min(0).max(0.15),
});

const responsiveBackgroundVariantSchema = z.object({
  assetId: z.string().min(1),
  bundle: z.enum(['background_landscape', 'background_portrait']),
  focalX: z.number().min(0).max(1),
  focalY: z.number().min(0).max(1),
});

const neonBackgroundSchema = z
  .object({
    referenceWidth: positiveInt,
    referenceHeight: positiveInt,
    restingAlpha: alpha,
    placements: z.array(neonPlacementSchema).min(1).max(16),
    quality: z.object({
      high: neonQualitySchema,
      balanced: neonQualitySchema,
      low: neonQualitySchema,
    }),
    effects: z.object({
      fadeDurationMs: z.number().int().min(0).max(2_000),
      pulseDurationMs: z.number().int().positive().max(3_000),
      pulseScale: z.number().min(1).max(1.25),
      shakeDurationMs: z.number().int().positive().max(2_000),
      shakeAmplitudePx: z.number().min(0).max(20),
      moveDurationMs: z.number().int().positive().max(3_000),
      moveDistanceRatio: z.number().positive().max(0.3),
    }),
    movementProfiles: z.object({
      default: neonMovementProfileSchema,
      freeSpins: neonMovementProfileSchema,
    }),
    glow: z.object({
      durationMs: z.number().int().positive().max(3_000),
      intensity: alpha,
      scale: z.number().min(1).max(1.3),
      tierIntensity: z.object({
        small: alpha,
        medium: alpha,
        big: alpha,
        jackpot: alpha,
      }),
    }),
    sequences: z.array(neonSequenceSchema).min(6).max(16),
    tierPolicy: z.object({
      small: z.string().min(1).nullable(),
      medium: z.string().min(1).nullable(),
      big: z.string().min(1).nullable(),
      jackpot: z.string().min(1).nullable(),
    }),
  })
  .superRefine((neon, context) => {
    const targetIds = neon.placements.map(({ id }) => id);
    if (new Set(targetIds).size !== targetIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['placements'],
        message: 'neon placement ids must be unique',
      });
    }
    neon.placements.forEach((placement, index) => {
      if (new Set(placement.groups).size !== placement.groups.length) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['placements', index, 'groups'],
          message: 'neon placement groups must be unique',
        });
      }
    });
    const groups = new Set(neon.placements.flatMap(({ groups: targetGroups }) => targetGroups));
    const sequenceIds = neon.sequences.map(({ id }) => id);
    if (new Set(sequenceIds).size !== sequenceIds.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sequences'],
        message: 'neon sequence ids must be unique',
      });
    }
    neon.sequences.forEach((sequence, index) => {
      if (sequence.group !== 'all' && !groups.has(sequence.group)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sequences', index, 'group'],
          message: 'neon sequence group must reference a configured placement group or all',
        });
      }
    });
    const knownSequences = new Set(sequenceIds);
    Object.entries(neon.tierPolicy).forEach(([tier, sequenceId]) => {
      if (sequenceId !== null && !knownSequences.has(sequenceId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tierPolicy', tier],
          message: 'neon tier policy must reference a configured sequence',
        });
      }
    });
  });

const paylineStyleSchema = z.object({
  color: color,
  alpha,
  width: positive,
  // Bright top stroke drawn over the soft base stroke.
  topColor: color.default(0xffffff),
  topAlpha: alpha.default(1),
  topWidth: positive.default(2),
  // Marching-dash pattern on the top stroke, in local px.
  dashLength: positive.default(14),
  gapLength: positive.default(10),
  // Dash offset sweep speed, in local px/sec (drives PaylineView.update()).
  sweepSpeed: positive.default(90),
  nodeRadius: positive.default(7),
  nodeColor: color.default(0xffffff),
  nodeStrokeColor: color.default(0xffd54a),
  nodePulseScale: z.number().min(1).max(2).default(1.35),
});

const floatingScoreStyleSchema = z.object({
  fontFamily: z.string().min(1),
  fontSize: positive,
  color,
  strokeColor: color,
  strokeWidth: nonNegative,
  risePx: positive,
  durationMs: positive,
  peakScale: z.number().min(1).max(2),
});

const heroTextStyleSchema = z.object({
  fontFamily: z.string().min(1),
  bigFontSize: positive,
  jackpotFontSize: positive,
  bigColor: color,
  jackpotColor: color,
  strokeColor: color,
  strokeWidth: nonNegative,
  letterSpacing: z.number().nonnegative(),
  shadowColor: color,
  shadowAlpha: alpha,
  shadowBlur: nonNegative,
  shadowDistance: nonNegative,
});

/** Free-spins progress presentation. The awaited intro transition lives with reel presentation. */
const freeSpinsPresentationSchema = z
  .object({
    /** `{n}` is replaced for the Spin control's screen-reader status. */
    remainingAccessibleLabel: z.string().min(1).max(48),
    spinButtonCountFontSize: positive,
    spinButtonCountColor: color,
    spinButtonSurfaceColor: color,
    spinButtonSurfaceAlpha: alpha,
    spinButtonSurfaceRadiusRatio: z.number().positive().max(0.45),
    spinButtonRingEnabled: z.boolean().default(false),
    spinButtonRingColor: color,
    spinButtonRingWidth: z.number().positive().max(12),
    spinButtonRingTrackColor: color,
    spinButtonRingTrackAlpha: alpha,
    spinButtonRingTrackWidth: z.number().positive().max(16),
    spinButtonRingRadiusRatio: z.number().positive().max(0.48),
    spinButtonRingDashCount: z.number().int().min(6).max(32),
    spinButtonRingDashRatio: z.number().min(0.2).max(0.8),
    spinButtonRingAngularSpeedDegPerSecond: z.number().positive().max(720),
    spinButtonRingEaseInDurationMs: z.number().positive().max(2_000),
    spinButtonRingEaseOutDurationMs: z.number().positive().max(2_000),
    noWinInterSpinDelayMs: z.number().int().nonnegative().max(5_000),
    spinButtonGlowColor: color,
    spinButtonGlowAlpha: alpha,
  })
  .superRefine((config, context) => {
    if (config.spinButtonRingRadiusRatio <= config.spinButtonSurfaceRadiusRatio) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['spinButtonRingRadiusRatio'],
        message: 'spinButtonRingRadiusRatio must exceed spinButtonSurfaceRadiusRatio',
      });
    }
  });

const layoutModeSchema = z.object({
  topReserve: nonNegative,
  panelInset: nonNegative,
  sideMarginRatio,
  reelAspect: positive,
  gap: nonNegative,
  railWidth: nonNegative,
  buttonRowHeight: nonNegative,
  controlSize: positive,
  spinSize: positive,
});

const mobilePortraitLayoutModeSchema = layoutModeSchema.extend({
  /** Reel-only width cap; keeps the control panel margin independently art-directable. */
  reelSideMarginRatio: sideMarginRatio,
});

const desktopLayoutModeSchema = layoutModeSchema
  .extend({
    compactSideMarginRatio: sideMarginRatio,
    compactAspectRatio: positive,
    wideAspectRatio: positive,
  })
  .superRefine((layout, context) => {
    if (layout.compactSideMarginRatio > layout.sideMarginRatio) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['compactSideMarginRatio'],
        message: 'compactSideMarginRatio must not exceed sideMarginRatio',
      });
    }
    if (layout.compactAspectRatio >= layout.wideAspectRatio) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['compactAspectRatio'],
        message: 'compactAspectRatio must be less than wideAspectRatio',
      });
    }
  });

const iconOffsetSchema = z.object({
  x: z.number(),
  y: z.number(),
});

const UiConfigObjectSchema = z.object({
  theme: z.object({
    background: color,
    surface: color,
    border: color,
    textPrimary: color,
    textSecondary: color,
    textMuted: color,
    accent: color,
    status: color,
    danger: color,
    glow: color,
    overlay: color,
  }),
  layoutPolicy: z.object({
    mobileBreakpoint: positiveInt,
    tabletBreakpoint: positiveInt,
    tabletLandscapeMaxAspectRatio: z.number().gt(1).max(2),
    desktopSingleRowMinWidth: positive,
    minTargetSize: positive,
    minSpinTargetSize: positive,
    emergencyTargetSize: positive,
    minReelHeight: positive,
    compactStatsHeight: positive,
    footerHideHeight: positive,
    maxDevicePixelRatio: z.number().min(1).max(4),
  }),
  modal: z.object({
    backdropAlpha: alpha,
    cardPadding: positive,
    cardRadius: nonNegative,
    strokeWidth: positive,
    closeDiameter: positive,
    closeEdgeInset: nonNegative,
    viewportMargin: positive,
    settingsWidth: positive,
    gameInfoWidth: positive,
    autoplayWidth: positive,
    betWidth: positive,
    errorWidth: positive,
    settingsRowGap: positive,
    settingsHeaderHeight: positive,
    headerHeight: positive,
    headerFontSize: positive,
    bodyFontSize: positive,
    supportFontSize: positive,
    sectionGap: nonNegative,
    enterDurationMs: nonNegative,
    exitDurationMs: nonNegative,
  }),
  autospinOptions: z.object({
    counts: z.array(positiveInt).min(1),
    defaultCount: positiveInt,
    interSpinDelayMs: z.number().int().nonnegative().max(5_000),
    buttonSize: positive,
    buttonHeight: positive,
    actionHeight: positive,
    gap: nonNegative,
    cancelWidth: positive,
  }),
  hudCluster: z.object({
    buttonDiameter: positive,
    spinDiameter: positive,
    roundButtonBackgroundTextureId: z.string().min(1).optional(),
    spinButtonBackgroundTextureId: z.string().min(1),
    dockBackgroundTextureId: z.string().min(1),
    portraitDockBackgroundTextureId: z.string().min(1),
    portraitPanelBackgroundTextureId: z.string().min(1),
    portraitInfoBackgroundTextureId: z.string().min(1),
    mobileLandscapePanelBackgroundTextureId: z.string().min(1),
    infoWidth: positive,
    infoHeight: positive,
    minInfoWidth: positive,
    rowButtonGap: nonNegative,
    railButtonGap: nonNegative,
    railPaddingRatio: z.number().min(0).max(0.4),
    buttonPanelPaddingRatio: z.number().min(0).max(0.25),
    stackedInfoGap: nonNegative,
    mobileLandscapeInfoXRatio: z.number().min(0).max(1),
    mobileLandscapeInfoYRatio: z.number().min(0).max(1),
    desktopDesign: z.object({
      width: positive,
      bodyHeight: positive,
      maxScale: z.number().positive().max(1),
      maxViewportWidthRatio: z.number().positive().max(1),
      controlDiameter: positive,
      spinMaxDisplayDiameter: positive,
      spinViewportRatio: z.number().positive().max(0.25),
      infoWidth: positive,
      infoHeight: positive,
      secondaryIconScale: z.number().min(0.5).max(1),
      spinIconScale: z.number().min(0.5).max(1),
      infoContentScale: z.number().min(0.5).max(1),
      controlCentersX: z.object({
        menu: nonNegative,
        info: nonNegative,
        betMinus: nonNegative,
        betPlus: nonNegative,
        spin: nonNegative,
        quickSpin: nonNegative,
        autoplay: nonNegative,
      }),
      readoutCentersX: z.object({
        bet: nonNegative,
        win: nonNegative,
        balance: nonNegative,
      }),
      separatorXs: z.tuple([nonNegative, nonNegative]),
    }),
    portraitDesign: z.object({
      statusWidth: positive,
      statusHeight: positive,
      statusMaxScale: z.number().positive().max(2),
      dockWidth: positive,
      dockHeight: positive,
      dockMaxScale: z.number().positive().max(2),
      topActionsHeight: positive,
      topActionEdgeInset: nonNegative,
      titleToReelGap: nonNegative,
      reelToStatusGap: nonNegative,
      infoWidth: positive,
      infoHeight: positive,
      menuDiameter: positive,
      controlDiameter: positive,
      spinDiameter: positive,
      controlCenterY: nonNegative,
      spinCenterY: nonNegative,
      controlCentersX: z.object({
        betMinus: nonNegative,
        betPlus: nonNegative,
        spin: nonNegative,
        quickSpin: nonNegative,
        autoplay: nonNegative,
      }),
      readoutCentersX: z.object({
        bet: nonNegative,
        win: nonNegative,
        balance: nonNegative,
      }),
      separatorXs: z.tuple([nonNegative, nonNegative]),
    }),
    mobileLandscapeDesign: z.object({
      railWidth: positive,
      railHeight: positive,
      maxScale: z.number().positive().max(1),
      edgeInset: nonNegative,
      reelGap: nonNegative,
      textScale: z.number().positive().max(1),
      menuCenterY: nonNegative,
      infoCenterY: nonNegative,
      infoWidth: positive,
      infoHeight: positive,
      readoutCentersY: z.object({
        bet: nonNegative,
        win: nonNegative,
        balance: nonNegative,
      }),
      separatorYs: z.tuple([nonNegative, nonNegative, nonNegative]),
    }),
    iconScaleRatio: z.number().min(0.2).max(0.9),
    spinIconScaleRatio: z.number().min(0.2).max(0.9),
    activeIconScaleRatio: z.number().min(0.5).max(1),
    activeIconTint: color,
    iconOffsets: z.object({
      menu: iconOffsetSchema,
      quickSpin: iconOffsetSchema,
      autoplay: iconOffsetSchema,
      coinOptions: iconOffsetSchema,
      spin: iconOffsetSchema,
    }),
    infoBackgroundTextureId: z.string().min(1),
    desktopInfoBackgroundTextureId: z.string().min(1),
    desktopSpinButtonBackgroundTextureId: z.string().min(1),
    desktopSpinIconTextureId: z.string().min(1),
    panelBackgroundTextureId: z.string().min(1),
    desktopPanelBackgroundTextureId: z.string().min(1),
  }),
  statsRail: z.object({
    height: positive,
    portraitHeight: positive,
    desktopOutlineColor: color,
    desktopOutlineWidth: nonNegative,
    desktopOutlineRadius: nonNegative,
    typography: z.object({
      desktop: statsRailTypographySchema,
      compactDesktop: statsRailTypographySchema,
      mobilePortrait: statsRailTypographySchema,
      mobileLandscape: statsRailTypographySchema,
    }),
    separatorColor: color,
    separatorWidth: positive,
    separatorHeightRatio: z.number().positive().max(1),
    compactBetXRatio: readoutXRatio,
    compactWinXRatio: readoutXRatio,
    compactBalanceXRatio: readoutXRatio,
    mobileLandscapeBetXRatio: readoutXRatio,
    mobileLandscapeWinXRatio: readoutXRatio,
    mobileLandscapeBalanceXRatio: readoutXRatio,
  }),
  footerMeta: z.object({
    enabled: z.boolean(),
    gameName: z.string().min(1),
    height: positive,
    mobileLandscapeMinHeight: positive,
    fontSize: positive,
    clockFormat: z.enum(['HH:mm']),
  }),
  gameTitle: z.object({
    desktopHeight: positive,
    portraitHeight: positive,
    gapAfter: nonNegative,
    topInset: nonNegative,
    titleFontSize: positive,
    subtitleFontSize: positive,
    portraitScale: z.number().positive().max(1),
    titleWordGap: nonNegative,
    titleGlyphOffsets: z.array(z.number().min(-16).max(16)).max(64),
    titleGlyphYOffsets: z.array(z.number().min(-16).max(16)).max(64),
    titleGlyphRotationsDeg: z.array(z.number().min(-8).max(8)).max(64),
    titleGlyphScales: z.array(z.number().min(0.9).max(1.1)).max(64),
    subtitleLetterSpacing: nonNegative,
    crownVisible: z.boolean(),
    crownWidth: positive,
    crownHeight: positive,
    crownGap: nonNegative,
    crownColor: color,
    crownStrokeWidth: positive,
    lineGap: nonNegative,
    letterSpacing: nonNegative,
    titleColor: color,
    subtitleColor: color,
    titleStrokeColor: color,
    titleStrokeWidth: nonNegative,
    titleShadowAlpha: alpha,
    titleShadowBlur: nonNegative,
    titleShadowDistance: nonNegative,
    subtitleStrokeColor: color,
    subtitleStrokeWidth: nonNegative,
    subtitleRibbonColor: color,
    subtitleRibbonAlpha: alpha,
    subtitleRibbonStrokeColor: color,
    subtitleRibbonStrokeWidth: nonNegative,
    subtitleRibbonWidth: positive,
    subtitleRibbonHeight: positive,
    subtitleRibbonRadius: nonNegative,
    subtitleOrnamentColor: color,
    subtitleOrnamentLength: nonNegative,
    subtitleOrnamentGap: nonNegative,
    subtitleOrnamentDotRadius: nonNegative,
    entranceDurationMs: nonNegative,
    letterStaggerMs: nonNegative,
    entranceScale: z.number().positive().max(1),
    entranceEasing: z.enum(['linear', 'easeOutBack']),
    fadeOutDurationMs: nonNegative,
    shakeDurationMs: positive,
    shakeStaggerMs: nonNegative,
    shakeAmplitudePx: nonNegative,
    shakeScale: z.number().min(1).max(1.25),
    shakeOscillations: z.number().int().min(1).max(6),
    shakeEasing: z.enum(['linear', 'easeOutCubic']),
    defaultStylePresetId: z.string().min(1),
    defaultAnimationPresetId: z.string().min(1),
    configuredOrder: z.array(z.number().int().min(0).max(63)).min(1).max(64),
    fontPresets: z.array(titleFontPresetSchema).min(3).max(8),
    stylePresets: z.array(titleStylePresetSchema).min(3).max(12),
    animationPresets: z.array(titleAnimationPresetSchema).min(3).max(12),
    tierPolicy: z.object({
      medium: titleEffectCommand,
      big: titleEffectCommand,
      jackpot: titleEffectCommand,
    }),
  }),
  betPanel: z.object({
    title: z.string().min(1),
    backdropAlpha: alpha,
    topOffset: nonNegative,
    portraitBottomInset: nonNegative,
    height: positive,
    portraitHeight: positive,
    optionHeight: positive,
    optionGap: nonNegative,
    minOptionWidth: positive,
    optionFontSize: positive,
  }),
  paytableRules: z.object({
    title: z.string().min(1),
    versionText: z.string().min(1),
    payoutsSubtitle: z.string().min(1),
    rulesHeading: z.string().min(1),
    wildCallout: z.object({
      title: z.string().min(1),
      body: z.string().min(1),
      textureId: z.string().min(1),
    }),
    scatterCallout: z.object({
      title: z.string().min(1),
      body: z.string().min(1),
      textureId: z.string().min(1),
    }),
    rules: z.array(z.string().min(1)).min(1),
    availabilityNote: z.string().min(1),
    /**
     * Shown above the table while the numbers are measured rather than server-supplied.
     * `{date}` is replaced with the paytable's measuredAt. Payouts themselves live in
     * paytable.config.json, keyed by real symbol ids — never as free text here.
     */
    provisionalNote: z.string().min(1),
  }),
  winPresentation: z.object({
    payline: paylineStyleSchema,
    celebrationPreset: z.string().min(1),
    floatingScore: floatingScoreStyleSchema,
    heroText: heroTextStyleSchema,
  }),
  freeSpins: freeSpinsPresentationSchema,
  backgrounds: z.object({
    byLayoutMode: z.object({
      desktop: responsiveBackgroundVariantSchema,
      mobileLandscape: responsiveBackgroundVariantSchema,
      mobilePortrait: responsiveBackgroundVariantSchema,
    }),
    portraitFocus: z.object({
      color,
      outerAlpha: alpha,
      innerAlpha: alpha,
      edgeInsetRatio: z.number().min(0).max(0.25),
      verticalPadding: nonNegative,
      featherSize: nonNegative,
      cornerRadius: nonNegative,
    }),
    neon: neonBackgroundSchema,
  }),
  splash: z.object({
    title: z.string().min(1).max(64),
    subtitle: z.string().min(1).max(32),
    backgroundFocalX: z.number().min(0).max(1),
    backgroundFocalY: z.number().min(0).max(1),
    minimumVisibleMs: z.number().int().nonnegative().max(10_000),
  }),
  layoutModes: z.object({
    mobilePortrait: mobilePortraitLayoutModeSchema,
    mobileLandscape: layoutModeSchema,
    desktop: desktopLayoutModeSchema,
  }),
});

const UiConfigSchema = UiConfigObjectSchema.superRefine((config, context) => {
  const { layoutPolicy, hudCluster, layoutModes, autospinOptions, betPanel } = config;
  const { gameTitle } = config;
  const fontIds = gameTitle.fontPresets.map(({ id }) => id);
  const styleIds = gameTitle.stylePresets.map(({ id }) => id);
  const animationIds = gameTitle.animationPresets.map(({ id }) => id);
  if (new Set(gameTitle.configuredOrder).size !== gameTitle.configuredOrder.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['gameTitle', 'configuredOrder'],
      message: 'configuredOrder must contain unique target indexes',
    });
  }
  for (const [path, ids] of [
    ['fontPresets', fontIds],
    ['stylePresets', styleIds],
    ['animationPresets', animationIds],
  ] as const) {
    if (new Set(ids).size !== ids.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['gameTitle', path],
        message: `${path} ids must be unique`,
      });
    }
  }
  const knownFonts = new Set(fontIds);
  gameTitle.stylePresets.forEach((preset, index) => {
    for (const field of ['titleFontId', 'subtitleFontId'] as const) {
      if (!knownFonts.has(preset[field])) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['gameTitle', 'stylePresets', index, field],
          message: `${field} must reference a configured font preset`,
        });
      }
    }
  });
  if (!new Set(styleIds).has(gameTitle.defaultStylePresetId)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['gameTitle', 'defaultStylePresetId'],
      message: 'defaultStylePresetId must reference a configured style preset',
    });
  }
  if (!new Set(animationIds).has(gameTitle.defaultAnimationPresetId)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['gameTitle', 'defaultAnimationPresetId'],
      message: 'defaultAnimationPresetId must reference a configured animation preset',
    });
  }
  if (layoutPolicy.desktopSingleRowMinWidth <= layoutPolicy.mobileBreakpoint) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['layoutPolicy', 'desktopSingleRowMinWidth'],
      message: 'desktopSingleRowMinWidth must exceed mobileBreakpoint',
    });
  }
  if (layoutPolicy.tabletBreakpoint <= layoutPolicy.mobileBreakpoint) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['layoutPolicy', 'tabletBreakpoint'],
      message: 'tabletBreakpoint must exceed mobileBreakpoint',
    });
  }
  if (layoutPolicy.minSpinTargetSize < layoutPolicy.minTargetSize) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['layoutPolicy', 'minSpinTargetSize'],
      message: 'minSpinTargetSize must be at least minTargetSize',
    });
  }
  if (hudCluster.minInfoWidth > hudCluster.infoWidth) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'minInfoWidth'],
      message: 'minInfoWidth must not exceed infoWidth',
    });
  }
  if (layoutPolicy.minTargetSize > hudCluster.infoHeight) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'infoHeight'],
      message: 'infoHeight must be at least minTargetSize',
    });
  }
  const desktopDesign = hudCluster.desktopDesign;
  const designCoordinates = [
    ...Object.entries(desktopDesign.controlCentersX),
    ...Object.entries(desktopDesign.readoutCentersX),
    ...desktopDesign.separatorXs.map((value, index) => [`separator${index + 1}`, value] as const),
  ];
  for (const [name, value] of designCoordinates) {
    if (value > desktopDesign.width) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hudCluster', 'desktopDesign', name],
        message: `${name} must be inside desktopDesign.width`,
      });
    }
  }
  if (desktopDesign.separatorXs[0] >= desktopDesign.separatorXs[1]) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'desktopDesign', 'separatorXs'],
      message: 'desktop separators must be ordered from left to right',
    });
  }
  if (desktopDesign.controlCentersX.spin !== desktopDesign.width / 2) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'desktopDesign', 'controlCentersX', 'spin'],
      message: 'desktop Spin must remain centered in the authored cabinet',
    });
  }
  if (
    desktopDesign.controlDiameter > desktopDesign.bodyHeight ||
    desktopDesign.infoHeight > desktopDesign.bodyHeight
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'desktopDesign', 'bodyHeight'],
      message: 'desktop bodyHeight must fit secondary controls and the Info pill',
    });
  }
  const portraitDesign = hudCluster.portraitDesign;
  if (
    Object.values(portraitDesign.controlCentersX).some((value) => value > portraitDesign.dockWidth)
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'portraitDesign', 'controlCentersX'],
      message: 'portrait control coordinates must remain inside portraitDesign.dockWidth',
    });
  }
  if (
    [...Object.values(portraitDesign.readoutCentersX), ...portraitDesign.separatorXs].some(
      (value) => value > portraitDesign.statusWidth,
    )
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'portraitDesign', 'readoutCentersX'],
      message: 'portrait status coordinates must remain inside portraitDesign.statusWidth',
    });
  }
  if (portraitDesign.controlCentersX.spin !== portraitDesign.dockWidth / 2) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'portraitDesign', 'controlCentersX', 'spin'],
      message: 'portrait Spin must remain centered in the authored dock',
    });
  }
  if (portraitDesign.statusWidth / portraitDesign.statusHeight <= 1) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'portraitDesign'],
      message: 'portrait status frame must be a horizontal fixed-aspect composition',
    });
  }
  const mobileLandscapeDesign = hudCluster.mobileLandscapeDesign;
  if (
    [
      mobileLandscapeDesign.menuCenterY,
      mobileLandscapeDesign.infoCenterY,
      ...Object.values(mobileLandscapeDesign.readoutCentersY),
      ...mobileLandscapeDesign.separatorYs,
    ].some((value) => value > mobileLandscapeDesign.railHeight)
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'mobileLandscapeDesign'],
      message: 'mobile-landscape coordinates must remain inside the authored rail height',
    });
  }
  if (
    mobileLandscapeDesign.separatorYs[0] >= mobileLandscapeDesign.separatorYs[1] ||
    mobileLandscapeDesign.separatorYs[1] >= mobileLandscapeDesign.separatorYs[2]
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'mobileLandscapeDesign', 'separatorYs'],
      message: 'mobile-landscape separators must be ordered from top to bottom',
    });
  }
  if (!autospinOptions.counts.includes(autospinOptions.defaultCount)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['autospinOptions', 'defaultCount'],
      message: 'defaultCount must be one of counts',
    });
  }
  if (new Set(autospinOptions.counts).size !== autospinOptions.counts.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['autospinOptions', 'counts'],
      message: 'counts must contain unique values',
    });
  }
  if (layoutPolicy.minTargetSize > autospinOptions.buttonHeight) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['autospinOptions', 'buttonHeight'],
      message: 'buttonHeight must be at least minTargetSize',
    });
  }
  if (layoutPolicy.minTargetSize > autospinOptions.actionHeight) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['autospinOptions', 'actionHeight'],
      message: 'actionHeight must be at least minTargetSize',
    });
  }
  if (layoutPolicy.minTargetSize > betPanel.optionHeight) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['betPanel', 'optionHeight'],
      message: 'optionHeight must be at least minTargetSize',
    });
  }
  if (layoutPolicy.minTargetSize > betPanel.minOptionWidth) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['betPanel', 'minOptionWidth'],
      message: 'minOptionWidth must be at least minTargetSize',
    });
  }
  for (const [modeName, mode] of Object.entries(layoutModes)) {
    if (layoutPolicy.minTargetSize > mode.controlSize) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['layoutModes', modeName, 'controlSize'],
        message: 'controlSize must be at least minTargetSize',
      });
    }
    if (layoutPolicy.minSpinTargetSize > mode.spinSize) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['layoutModes', modeName, 'spinSize'],
        message: 'spinSize must be at least minSpinTargetSize',
      });
    }
  }
  const naturalDesktopControlHeight = Math.max(
    layoutModes.desktop.controlSize,
    layoutModes.desktop.spinSize,
    hudCluster.infoHeight,
  );
  if (layoutModes.desktop.buttonRowHeight < naturalDesktopControlHeight) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['layoutModes', 'desktop', 'buttonRowHeight'],
      message: 'desktop buttonRowHeight must fit the natural desktop control height',
    });
  }
  const minimumCompactDesktopRowWidth =
    layoutPolicy.minTargetSize * 5 +
    layoutPolicy.minSpinTargetSize +
    hudCluster.minInfoWidth +
    4 * 6;
  const minimumCompactDesktopAvailableWidth =
    (layoutPolicy.mobileBreakpoint - layoutModes.desktop.panelInset * 2) *
    (1 - hudCluster.buttonPanelPaddingRatio * 2);
  if (minimumCompactDesktopRowWidth > minimumCompactDesktopAvailableWidth) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hudCluster', 'minInfoWidth'],
      message: 'minimum compact desktop controls and padding must fit at mobileBreakpoint',
    });
  }
});

export type LayoutPolicyConfig = z.infer<typeof UiConfigSchema>['layoutPolicy'];
export type UiThemeConfig = z.infer<typeof UiConfigSchema>['theme'];
export type ModalLayoutConfig = z.infer<typeof UiConfigSchema>['modal'];
export type AutospinOptionsLayoutConfig = z.infer<typeof UiConfigSchema>['autospinOptions'];
export type HudClusterLayoutConfig = z.infer<typeof UiConfigSchema>['hudCluster'];
export type StatsRailLayoutConfig = z.infer<typeof UiConfigSchema>['statsRail'];
export type StatsRailTypographyProfileName = keyof StatsRailLayoutConfig['typography'];
export type StatsRailTypographyConfig =
  StatsRailLayoutConfig['typography'][StatsRailTypographyProfileName];
export type FooterMetaLayoutConfig = z.infer<typeof UiConfigSchema>['footerMeta'];
export type GameTitleLayoutConfig = z.infer<typeof UiConfigSchema>['gameTitle'];
export type BetPanelLayoutConfig = z.infer<typeof UiConfigSchema>['betPanel'];
export type PaytableRulesConfig = z.infer<typeof UiConfigSchema>['paytableRules'];
export type WinPresentationStyleConfig = z.infer<typeof UiConfigSchema>['winPresentation'];
export type FreeSpinsPresentationConfig = z.infer<typeof UiConfigSchema>['freeSpins'];
export type BackgroundLayoutConfig = z.infer<typeof UiConfigSchema>['backgrounds'];
export type NeonBackgroundConfig = BackgroundLayoutConfig['neon'];
export type SplashPresentationConfig = z.infer<typeof UiConfigSchema>['splash'];
export type SlotModeLayoutConfig = z.infer<typeof UiConfigSchema>['layoutModes'];
export type UiConfigData = z.infer<typeof UiConfigSchema>;

export class UiConfig {
  readonly layoutPolicy: LayoutPolicyConfig;
  readonly theme: UiThemeConfig;
  readonly modal: ModalLayoutConfig;
  readonly autospinOptions: AutospinOptionsLayoutConfig;
  readonly hudCluster: HudClusterLayoutConfig;
  readonly statsRail: StatsRailLayoutConfig;
  readonly footerMeta: FooterMetaLayoutConfig;
  readonly gameTitle: GameTitleLayoutConfig;
  readonly betPanel: BetPanelLayoutConfig;
  readonly paytableRules: PaytableRulesConfig;
  readonly winPresentation: WinPresentationStyleConfig;
  readonly freeSpins: FreeSpinsPresentationConfig;
  readonly backgrounds: BackgroundLayoutConfig;
  readonly splash: SplashPresentationConfig;
  readonly layoutModes: SlotModeLayoutConfig;

  constructor(raw: unknown = uiConfigRaw) {
    const parsed = UiConfigSchema.safeParse(raw);
    if (!parsed.success) {
      throw new GameError('CONFIG_LOAD_FAILED', `Invalid UI config: ${parsed.error.message}`, {
        cause: parsed.error,
      });
    }
    this.layoutPolicy = parsed.data.layoutPolicy;
    this.theme = parsed.data.theme;
    this.modal = parsed.data.modal;
    this.autospinOptions = parsed.data.autospinOptions;
    this.hudCluster = parsed.data.hudCluster;
    this.statsRail = parsed.data.statsRail;
    this.footerMeta = parsed.data.footerMeta;
    this.gameTitle = parsed.data.gameTitle;
    this.betPanel = parsed.data.betPanel;
    this.paytableRules = parsed.data.paytableRules;
    this.winPresentation = parsed.data.winPresentation;
    this.freeSpins = parsed.data.freeSpins;
    this.backgrounds = parsed.data.backgrounds;
    this.splash = parsed.data.splash;
    this.layoutModes = parsed.data.layoutModes;
  }
}
